/**
 * Combined lists — see the firmas of the whole group in one list (skupina-vlastnictvi-plan §6.5).
 *
 * The server decides the scope (`GET /v1/skupina-rozsah`: the group's firmas where the caller holds
 * read on the module); the client only asks for `rozsah=skupina` and remembers the choice per module.
 * A row of another firma opens (and its plain edit saves) in ITS firma on the server — `zapis` tells the UI
 * whether the caller may also write there, so the edit buttons match what the API will allow.
 */

import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type RozsahModul = 'vozidla' | 'ojeta_vozidla';
export type Rozsah = 'aktivni' | 'skupina';

export interface RozsahFirma {
  id_firmy: number;
  nazev: string;
  /** The caller also holds write on the module in this firma. */
  zapis: boolean;
}

interface RozsahResponse {
  id_aktivni: number;
  firmy: RozsahFirma[];
}

const storageKey = (modul: RozsahModul) => `intranet:rozsah:${modul}`;

function readPreference(modul: RozsahModul): Rozsah {
  try {
    return window.localStorage.getItem(storageKey(modul)) === 'skupina' ? 'skupina' : 'aktivni';
  } catch {
    return 'aktivni';
  }
}

export function useSkupinaRozsahQuery(modul: RozsahModul, enabled = true) {
  return useQuery({
    queryKey: ['skupina-rozsah', modul],
    enabled,
    queryFn: ({ signal }) => api.get<RozsahResponse>(`/v1/skupina-rozsah?modul=${modul}`, { signal }),
    staleTime: 5 * 60_000,
  });
}

export interface RozsahState {
  /** What the list asks the API for. `skupina` only when the group really offers a second firma. */
  rozsah: Rozsah;
  setRozsah: (next: Rozsah) => void;
  /** At least two readable firmas: the chip is shown. */
  canSpan: boolean;
  firmy: RozsahFirma[];
}

export function useRozsah(modul: RozsahModul): RozsahState {
  const query = useSkupinaRozsahQuery(modul);
  const [preferred, setPreferred] = useState<Rozsah>(() => readPreference(modul));
  const firmy = query.data?.firmy ?? [];
  const canSpan = firmy.length >= 2;
  const setRozsah = useCallback(
    (next: Rozsah) => {
      setPreferred(next);
      try {
        window.localStorage.setItem(storageKey(modul), next);
      } catch {
        // private window / blocked storage: the choice just doesn't outlive the page
      }
    },
    [modul],
  );
  return {
    // While the scope is still loading the saved choice is requested as it is (the server computes the
    // scope itself), so the list doesn't fetch twice and flash; if the group offers only one firma, it falls back.
    rozsah: preferred === 'skupina' && (query.isPending || canSpan) ? 'skupina' : 'aktivni',
    setRozsah,
    canSpan,
    firmy,
  };
}
